/**
 * Share links for a board, a VIEW of it, and an ITEM on it (F-123).
 *
 * Kola runs both standalone in a browser AND embedded as a `<webview>` inside
 * the Rainvic desktop app, so the link a user hands a teammate must open the
 * right thing in either place. That is exactly what the desktop app's share
 * links already do: `https://…/open/kola/<boardId>` is a Firebase Hosting page
 * that forwards to the `rainvic://kola/<boardId>` deep link (which the app
 * registers with the OS), and falls back to a plain web page for anyone
 * without the app.
 *
 * The two constants below MIRROR `src/shared/constants.ts` in the enclosing
 * rainvic-tool repo (`SHARE_WEB_ORIGIN` / `SHARE_WEB_PATH`) — the source of
 * truth for the link format and the host the mobile App Links / Universal
 * Links config is wired to. They are duplicated rather than imported because
 * Kola is a SEPARATE npm install root with its own tsconfig (kola/CLAUDE.md):
 * it deliberately shares no build graph with the desktop app. If the desktop
 * app ever moves its share origin, this file moves with it.
 */
import { clampZoom, type Camera } from "@kola/shared";

const SHARE_WEB_ORIGIN = "https://rainvic-tool-electron.firebaseapp.com";
const SHARE_WEB_PATH = "/open";

/**
 * The shareable link for one board. `boardId` is URL-encoded, so an id with
 * URL-significant characters can never produce a link that parses as a
 * different board.
 */
export function boardShareLink(boardId: string): string {
  return `${SHARE_WEB_ORIGIN}${SHARE_WEB_PATH}/kola/${encodeURIComponent(boardId)}`;
}

/* ------------------------------------------------------------------ */
/* Views and items (F-123, 2026-09-04)                                 */
/* ------------------------------------------------------------------ */

/**
 * The query parameters a board link may carry, honoured by the board screen
 * (features/board/useShareDeepLink.ts) once the doc has synced:
 *
 *   ?v=<x>,<y>,<zoom>   — open at exactly this camera (a VIEW link);
 *   ?cell=<rowId>:<colId> — pan/zoom to this cell and select it (an ITEM link);
 *   ?el=<elementId>       — the same for a floating element.
 *
 * The desktop app's share page and deep-link handler pass these through to
 * the Kola URL it opens (src/renderer/lib/share-links.ts KOLA_LINK_PARAMS —
 * keep the two lists in step), so a link copied inside the app lands the
 * recipient on the same spot whether they open it in the app or a browser.
 */
export const VIEW_PARAM = "v";
export const CELL_PARAM = "cell";
export const ELEMENT_PARAM = "el";

/**
 * `x,y,zoom` — the camera, rounded so the link stays short: a tenth of a
 * board unit is under a screen pixel at any zoom the canvas allows, and three
 * decimals of zoom are finer than the wheel ever steps.
 */
export function encodeViewParam(camera: Camera): string {
  const num = (value: number, digits: number) =>
    String(Number(value.toFixed(digits))); // Number() drops trailing zeros
  return `${num(camera.x, 1)},${num(camera.y, 1)},${num(camera.zoom, 3)}`;
}

/** The camera a `v` param names, or null for anything malformed. Zoom is clamped. */
export function parseViewParam(raw: string | null | undefined): Camera | null {
  if (raw === null || raw === undefined) return null;
  const parts = raw.split(",");
  if (parts.length !== 3) return null;
  const [x, y, zoom] = parts.map((part) => Number(part));
  if (x === undefined || y === undefined || zoom === undefined) return null;
  if (![x, y, zoom].every((n) => Number.isFinite(n))) return null;
  if (zoom <= 0) return null;
  return { x, y, zoom: clampZoom(zoom) };
}

/** `rowId:colId`, each half encoded so a `:` inside an id cannot split it. */
export function encodeCellParam(rowId: string, colId: string): string {
  return `${encodeURIComponent(rowId)}:${encodeURIComponent(colId)}`;
}

export function parseCellParam(
  raw: string | null | undefined,
): { rowId: string; colId: string } | null {
  if (raw === null || raw === undefined) return null;
  const at = raw.indexOf(":");
  if (at <= 0 || at === raw.length - 1) return null;
  try {
    return { rowId: decodeURIComponent(raw.slice(0, at)), colId: decodeURIComponent(raw.slice(at + 1)) };
  } catch {
    return null;
  }
}

/** A board link plus query params, through the desktop app's share page. */
function boardLinkWith(boardId: string, params: Record<string, string>): string {
  const query = new URLSearchParams(params).toString();
  return `${boardShareLink(boardId)}?${query}`;
}

/** "Copy link to this view" — the board, opened at exactly this camera. */
export function boardViewShareLink(boardId: string, camera: Camera): string {
  return boardLinkWith(boardId, { [VIEW_PARAM]: encodeViewParam(camera) });
}

/** "Copy link to this cell" — the board, panned and zoomed onto the cell, selected. */
export function cellShareLink(boardId: string, rowId: string, colId: string): string {
  return boardLinkWith(boardId, { [CELL_PARAM]: encodeCellParam(rowId, colId) });
}

/** "Copy link" on a floating element — the same, for an element. */
export function elementShareLink(boardId: string, elementId: string): string {
  return boardLinkWith(boardId, { [ELEMENT_PARAM]: elementId });
}

/**
 * Copy text to the clipboard, reporting whether it worked. `navigator.clipboard`
 * is unavailable on an insecure origin and can be refused by permission policy,
 * and the caller has to tell the user which happened — so this resolves false
 * rather than throwing.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
